// src/components/chatbot/ChatbotFlowsPage.tsx
// Track E8 — flow CRUD + visual step editor.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Bot, ChevronDown, ChevronRight, Edit3, Eye, EyeOff,
  Loader2, MessageCircle, Plus, Power, RefreshCw, Send, Target,
  Trash2, UserCog, X,
} from 'lucide-react';

import {
  ApiError,
  createFlow, deleteFlow, listFlows, updateFlow,
  type ChatbotFlow, type FlowIn, type FlowStep, type StepType,
  type TriggerType,
} from '@/lib/api_chatbot';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

const TRIGGER_LABELS: Record<TriggerType, string> = {
  keywords: 'Keywords',
  regex: 'Regex',
  welcome: 'Bienvenida',
  fallback: 'Fallback',
  intent: 'Intent',
};

const STEP_META: Record<StepType, { label: string; icon: JSX.Element; cls: string }> = {
  message:  { label: 'Mensaje',   icon: <MessageCircle size={12} />, cls: 'bg-sky-500/20 text-sky-300' },
  question: { label: 'Pregunta',  icon: <Target size={12} />,        cls: 'bg-amber-500/20 text-amber-300' },
  handoff:  { label: 'Handoff',   icon: <UserCog size={12} />,       cls: 'bg-red-500/20 text-red-300' },
  complete: { label: 'Completar', icon: <Send size={12} />,          cls: 'bg-emerald-500/20 text-emerald-300' },
};


export function ChatbotFlowsPage({ addNotification }: Props) {
  const [rows, setRows] = useState<ChatbotFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ChatbotFlow | 'new' | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listFlows({ limit: 100 });
      setRows(out.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargaron flows',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: number) => {
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleActive = async (flow: ChatbotFlow) => {
    setBusy((b) => ({ ...b, [flow.id]: 'toggle' }));
    try {
      const updated = await updateFlow(flow.id, { is_active: !flow.is_active });
      setRows((r) => r.map((x) => (x.id === flow.id ? updated : x)));
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Error',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[flow.id]; return n; });
    }
  };

  const handleDelete = async (flow: ChatbotFlow) => {
    if (!window.confirm(`¿Eliminar flow "${flow.name}"?`)) return;
    setBusy((b) => ({ ...b, [flow.id]: 'delete' }));
    try {
      await deleteFlow(flow.id);
      addNotification?.({ type: 'success', title: 'Flow eliminado' });
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo eliminar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[flow.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bot className="text-emerald-400" /> Flows del chatbot
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Disparadores + pasos. El dispatcher corre al recibir cada mensaje;
            chatbot gana al MCP bridge cuando hay match.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setEditing('new')} className="o-btn o-btn-primary flex items-center gap-2">
            <Plus size={16} /> Nuevo flow
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-emerald-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Bot size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin flows configurados.</p>
          <p className="text-sm mt-1">
            Crea uno — sin flows, los mensajes inbound van directo al MCP bridge.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((flow) => {
            const op = busy[flow.id];
            const isOpen = expanded.has(flow.id);
            return (
              <div key={flow.id} className={`bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden ${!flow.is_active ? 'opacity-70' : ''}`}>
                <div className="p-3 flex items-center gap-3">
                  <button onClick={() => toggle(flow.id)} className="text-gray-400 hover:text-white">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white">{flow.name}</span>
                      <span className="text-xs text-gray-500 font-mono">{flow.slug}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-200">
                        {TRIGGER_LABELS[flow.trigger_type]}
                      </span>
                      <span className="text-xs text-gray-500">prioridad {flow.priority}</span>
                      <span className="text-xs text-gray-500">{flow.steps.length} paso{flow.steps.length === 1 ? '' : 's'}</span>
                      {flow.channels.length > 0 && (
                        <span className="text-xs text-gray-500">
                          canales: {flow.channels.join(', ')}
                        </span>
                      )}
                    </div>
                    {flow.description && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-1">{flow.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleActive(flow)}
                      disabled={!!op}
                      className={`o-btn o-btn-xs ${flow.is_active ? 'o-btn-ghost text-emerald-400' : 'o-btn-ghost text-gray-500'}`}
                    >
                      {op === 'toggle' ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                    </button>
                    <button onClick={() => setEditing(flow)} className="o-btn o-btn-xs o-btn-ghost">
                      <Edit3 size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(flow)}
                      disabled={!!op}
                      className="o-btn o-btn-xs o-btn-ghost text-red-400"
                    >
                      {op === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-gray-700 bg-gray-900/30">
                    <div className="pt-3 text-xs text-gray-400 uppercase">Triggers</div>
                    {flow.trigger_type === 'keywords' && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {flow.trigger_keywords.length === 0
                          ? <span className="text-sm text-gray-500">— sin keywords —</span>
                          : flow.trigger_keywords.map((k) => (
                              <span key={k} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-xs text-gray-200">
                                {k}
                              </span>
                            ))}
                      </div>
                    )}
                    {flow.trigger_type === 'regex' && (
                      <code className="text-sm text-sky-300">{flow.trigger_pattern || '—'}</code>
                    )}
                    {flow.trigger_type === 'intent' && (
                      <code className="text-sm text-purple-300">{flow.trigger_intent || '—'}</code>
                    )}

                    <div className="pt-3 text-xs text-gray-400 uppercase">Pasos</div>
                    {flow.steps.length === 0 ? (
                      <p className="text-sm text-gray-500 mt-1">Sin pasos todavía.</p>
                    ) : (
                      <ol className="mt-1 space-y-1">
                        {flow.steps.map((s, i) => {
                          const m = STEP_META[s.type] || STEP_META.message;
                          const preview = s.type === 'message'
                            ? s.body
                            : s.type === 'question'
                            ? s.prompt
                            : s.type === 'handoff'
                            ? s.reason || s.body
                            : '';
                          return (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className="text-gray-500 font-mono w-6">{i + 1}.</span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
                                {m.icon} {m.label}
                              </span>
                              <span className="text-gray-200 flex-1">{preview}</span>
                              {s.var && (
                                <span className="text-xs text-gray-500 font-mono">→ {'{{'}{s.var}{'}}'}</span>
                              )}
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <FlowEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Editor
// ═══════════════════════════════════════════════════════════════

function FlowEditor({
  initial, onClose, onSaved, notify,
}: {
  initial: ChatbotFlow | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState<FlowIn>({
    name: initial?.name || '',
    slug: initial?.slug || '',
    trigger_type: initial?.trigger_type || 'keywords',
    trigger_keywords: initial?.trigger_keywords || [],
    trigger_pattern: initial?.trigger_pattern || '',
    trigger_intent: initial?.trigger_intent || '',
    priority: initial?.priority ?? 100,
    channels: initial?.channels || [],
    steps: (initial?.steps || []) as FlowStep[],
    description: initial?.description || '',
    is_active: initial?.is_active ?? true,
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addKeyword = () => {
    const k = keywordInput.trim().toLowerCase();
    if (!k) return;
    setForm((f) => ({
      ...f,
      trigger_keywords: Array.from(new Set([...(f.trigger_keywords || []), k])),
    }));
    setKeywordInput('');
  };

  const removeKeyword = (k: string) => {
    setForm((f) => ({
      ...f, trigger_keywords: (f.trigger_keywords || []).filter((x) => x !== k),
    }));
  };

  const addStep = (type: StepType) => {
    const step: FlowStep = { type };
    if (type === 'message') step.body = '';
    if (type === 'question') { step.prompt = ''; step.var = `answer_${(form.steps || []).length + 1}`; }
    if (type === 'handoff') step.reason = '';
    setForm((f) => ({ ...f, steps: [...(f.steps || []), step] }));
  };

  const updateStep = (i: number, patch: Partial<FlowStep>) => {
    setForm((f) => ({
      ...f,
      steps: (f.steps || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  };

  const removeStep = (i: number) => {
    setForm((f) => ({
      ...f, steps: (f.steps || []).filter((_, idx) => idx !== i),
    }));
  };

  const moveStep = (i: number, dir: -1 | 1) => {
    const steps = [...(form.steps || [])];
    const target = i + dir;
    if (target < 0 || target >= steps.length) return;
    [steps[i], steps[target]] = [steps[target], steps[i]];
    setForm((f) => ({ ...f, steps }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit && initial) {
        await updateFlow(initial.id, form);
      } else {
        await createFlow({
          ...form,
          slug: (form.slug || '').toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        });
      }
      notify?.({ type: 'success', title: isEdit ? 'Flow actualizado' : 'Flow creado' });
      await onSaved();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'Error',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-4">
            {isEdit ? `Editar flow — ${initial?.name}` : 'Nuevo flow'}
          </h3>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({
                    ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                  }))}
                  disabled={isEdit}
                  required
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Descripción</label>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Trigger</label>
                <select
                  value={form.trigger_type}
                  onChange={(e) => setForm((f) => ({ ...f, trigger_type: e.target.value as TriggerType }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Prioridad</label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            {form.trigger_type === 'keywords' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Keywords</label>
                <div className="flex gap-2">
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                    placeholder="hola, precio, horario"
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  />
                  <button type="button" onClick={addKeyword} className="o-btn o-btn-ghost">Agregar</button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(form.trigger_keywords || []).map((k) => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-xs text-gray-200">
                      {k}
                      <button type="button" onClick={() => removeKeyword(k)}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {form.trigger_type === 'regex' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Patrón</label>
                <input
                  value={form.trigger_pattern}
                  onChange={(e) => setForm((f) => ({ ...f, trigger_pattern: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                />
              </div>
            )}
            {form.trigger_type === 'intent' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Intent tag</label>
                <input
                  value={form.trigger_intent}
                  onChange={(e) => setForm((f) => ({ ...f, trigger_intent: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono"
                />
              </div>
            )}

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">Pasos</label>
                <div className="flex gap-1">
                  {(Object.keys(STEP_META) as StepType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => addStep(t)}
                      className="o-btn o-btn-xs o-btn-ghost"
                      title={`Agregar paso ${STEP_META[t].label}`}
                    >
                      {STEP_META[t].icon} {STEP_META[t].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {(form.steps || []).map((s, i) => {
                  const m = STEP_META[s.type];
                  return (
                    <div key={i} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
                          {m.icon} {i + 1}. {m.label}
                        </span>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveStep(i, -1)} className="o-btn o-btn-xs o-btn-ghost" disabled={i === 0}>↑</button>
                          <button type="button" onClick={() => moveStep(i, 1)} className="o-btn o-btn-xs o-btn-ghost" disabled={i === (form.steps || []).length - 1}>↓</button>
                          <button type="button" onClick={() => removeStep(i)} className="o-btn o-btn-xs o-btn-ghost text-red-400">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      {s.type === 'message' && (
                        <textarea
                          value={s.body || ''}
                          onChange={(e) => updateStep(i, { body: e.target.value })}
                          placeholder="Texto del mensaje — puedes usar {{var}}"
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                        />
                      )}
                      {s.type === 'question' && (
                        <div className="space-y-2">
                          <textarea
                            value={s.prompt || ''}
                            onChange={(e) => updateStep(i, { prompt: e.target.value })}
                            placeholder="Pregunta que hace el bot"
                            rows={2}
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                          <input
                            value={s.var || ''}
                            onChange={(e) => updateStep(i, { var: e.target.value })}
                            placeholder="Variable para guardar la respuesta (e.g. name)"
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono text-sm"
                          />
                        </div>
                      )}
                      {s.type === 'handoff' && (
                        <div className="space-y-2">
                          <input
                            value={s.reason || ''}
                            onChange={(e) => updateStep(i, { reason: e.target.value })}
                            placeholder="Razón (ej: 'user asked for human')"
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                          <input
                            value={s.body || ''}
                            onChange={(e) => updateStep(i, { body: e.target.value })}
                            placeholder="Mensaje de despedida (opcional)"
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      )}
                      {s.type === 'complete' && (
                        <input
                          value={s.body || ''}
                          onChange={(e) => updateStep(i, { body: e.target.value })}
                          placeholder="Mensaje final (opcional)"
                          className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="flow-active"
                checked={!!form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-emerald-500 focus:ring-emerald-500"
              />
              <label htmlFor="flow-active" className="text-sm text-gray-300">Flow activo</label>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-gray-700">
              <button type="button" onClick={onClose} className="o-btn o-btn-ghost">Cancelar</button>
              <button type="submit" disabled={saving} className="o-btn o-btn-primary flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {isEdit ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatbotFlowsPage;
